import { coerceDateTime } from '../my-journeys/myJourneysModel';

export const NOTICE_TYPES = Object.freeze([
  'DECISION_VOTING_OPENED',
  'DECISION_CLOSED',
  'DECISION_CANCELLED',
  'SITUATION_CREATED',
  'SITUATION_ASSIGNED',
  'SITUATION_RESOLVED',
  'SITUATION_CANCELLED',
]);

export const NOTICE_STATUSES = Object.freeze(['UNREAD', 'READ', 'ACKNOWLEDGED']);
export const NOTICE_SOURCE_TYPES = Object.freeze(['DECISION', 'SITUATION']);

const TYPE_LABELS = Object.freeze({
  DECISION_VOTING_OPENED: '決策開始投票',
  DECISION_CLOSED: '決策已結束',
  DECISION_CANCELLED: '決策已取消',
  SITUATION_CREATED: '新的旅程狀況',
  SITUATION_ASSIGNED: '狀況已指派給你',
  SITUATION_RESOLVED: '狀況已解決',
  SITUATION_CANCELLED: '狀況已取消',
});

const STATUS_LABELS = Object.freeze({
  UNREAD: '未讀',
  READ: '已讀',
  ACKNOWLEDGED: '已確認收到',
});

const SOURCE_LABELS = Object.freeze({
  DECISION: '決策',
  SITUATION: '旅程狀況',
});

const CTA_LABELS = Object.freeze({
  DECISION_VOTING_OPENED: '查看決策並投票',
  DECISION_CLOSED: '查看決策紀錄',
  DECISION_CANCELLED: '查看決策紀錄',
  SITUATION_CREATED: '查看旅程狀況',
  SITUATION_ASSIGNED: '查看指派狀況',
  SITUATION_RESOLVED: '查看狀況紀錄',
  SITUATION_CANCELLED: '查看狀況紀錄',
});

export function asIdString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function isPositiveId(value) {
  return /^[1-9]\d*$/.test(asIdString(value));
}

export function noticeTypeLabel(noticeType) {
  const key = String(noticeType || '');
  if (TYPE_LABELS[key]) return TYPE_LABELS[key];
  return key ? `未知類型（${key}）` : '旅程通知';
}

export function noticeStatusLabel(status) {
  const key = String(status || '');
  if (STATUS_LABELS[key]) return STATUS_LABELS[key];
  return key ? `未知狀態（${key}）` : '未讀';
}

export function noticeSourceLabel(sourceType) {
  const key = String(sourceType || '');
  if (SOURCE_LABELS[key]) return SOURCE_LABELS[key];
  return key ? `未知來源（${key}）` : '';
}

export function noticeCtaLabel(noticeType) {
  const key = String(noticeType || '');
  return CTA_LABELS[key] || '查看來源';
}

export function isUnread(notice) {
  return String(notice?.noticeStatus || '') === 'UNREAD';
}

export function canAcknowledge(notice) {
  if (!notice?.ackRequired) return false;
  if (String(notice.noticeStatus || '') === 'ACKNOWLEDGED') return false;
  if (notice.acknowledgedAt) return false;
  return true;
}

export function noticeSourceRoute(notice) {
  const journeyId = asIdString(notice?.journeyId);
  const sourceId = asIdString(notice?.sourceId);
  const sourceType = String(notice?.sourceType || '');
  if (!isPositiveId(journeyId) || !isPositiveId(sourceId)) return '';
  if (sourceType === 'DECISION') return `/journeys/${journeyId}/decisions/${sourceId}`;
  if (sourceType === 'SITUATION') return `/journeys/${journeyId}/situations/${sourceId}`;
  return '';
}

export function unwrapNoticePage(payload) {
  if (Array.isArray(payload)) {
    return { items: payload, nextCursor: null, hasMore: false };
  }
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return {
    items,
    nextCursor: payload?.nextCursor || null,
    hasMore: Boolean(payload?.hasMore),
  };
}

export function normalizeNoticeCursor(value) {
  if (!value) return null;
  const createTime = coerceDateTime(value.createTime) || asIdString(value.createTime);
  const noticeId = asIdString(value.noticeId);
  if (!createTime || !isPositiveId(noticeId)) return null;
  return { createTime, noticeId };
}

export function normalizeNotice(value) {
  const noticeId = asIdString(value?.noticeId);
  const journeyId = asIdString(value?.journeyId);
  const sourceId = asIdString(value?.sourceId);
  const noticeType = String(value?.noticeType || '');
  const sourceType = String(value?.sourceType || '');
  const noticeStatus = String(value?.noticeStatus || 'UNREAD');
  const rowVersionRaw = value?.rowVersion;
  const rowVersion = Number.isFinite(Number(rowVersionRaw)) ? Number(rowVersionRaw) : 0;
  const route = noticeSourceRoute({ journeyId, sourceId, sourceType });
  return {
    noticeId,
    journeyId,
    sourceType,
    sourceId,
    noticeType,
    title: String(value?.title || '').trim() || noticeTypeLabel(noticeType),
    body: value?.body == null ? '' : String(value.body),
    severity: value?.severity == null ? '' : String(value.severity),
    noticeStatus,
    ackRequired: Boolean(value?.ackRequired),
    readAt: coerceDateTime(value?.readAt) || '',
    acknowledgedAt: coerceDateTime(value?.acknowledgedAt) || '',
    rowVersion,
    createTime: coerceDateTime(value?.createTime) || asIdString(value?.createTime),
    typeLabel: noticeTypeLabel(noticeType),
    statusLabel: noticeStatusLabel(noticeStatus),
    sourceLabel: noticeSourceLabel(sourceType),
    sourceRoute: route,
    ctaLabel: route ? noticeCtaLabel(noticeType) : '',
    unread: noticeStatus === 'UNREAD',
    canAcknowledge: canAcknowledge({
      ackRequired: Boolean(value?.ackRequired),
      noticeStatus,
      acknowledgedAt: value?.acknowledgedAt,
    }),
  };
}

export function normalizeNoticePage(payload) {
  const page = unwrapNoticePage(payload);
  return {
    items: page.items.map(normalizeNotice).filter((item) => isPositiveId(item.noticeId)),
    nextCursor: normalizeNoticeCursor(page.nextCursor),
    hasMore: Boolean(page.hasMore),
  };
}

export function normalizeUnreadCount(payload) {
  const raw = payload?.unreadCount;
  const count = Number.isFinite(Number(raw)) ? Math.max(0, Number(raw)) : 0;
  return {
    journeyId: asIdString(payload?.journeyId),
    unreadCount: count,
  };
}

export function unwrapNoticeCommand(payload) {
  const notice = payload?.notice || payload;
  return {
    notice: normalizeNotice(notice),
    noOp: Boolean(payload?.noOp),
  };
}

export function classifyNoticeError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (key === 'travel.journey.not.found') return 'journey-not-found';
  if (key === 'travel.collaboration.notice.not.found' || (code === 94002 && key.includes('notice'))) {
    return 'not-found';
  }
  if (code === 94001 || key === 'travel.collaboration.permission.denied') return 'forbidden';
  if (key === 'travel.collaboration.notice.concurrent.modification') return 'conflict';
  if (key === 'travel.collaboration.notice.acknowledgement.not.required') return 'ack-not-required';
  if (code === 94000 || key === 'travel.collaboration.integration.validation.failed') return 'validation';
  if (key.includes('not.found')) return 'not-found';
  return 'error';
}

export function noticeErrorMessage(cause, fallback) {
  const kind = classifyNoticeError(cause);
  if (kind === 'unauthorized') return '登入已過期，請重新登入。';
  if (kind === 'forbidden') return '你沒有權限查看或操作這些旅程通知。';
  if (kind === 'journey-not-found') return '找不到這趟旅程。';
  if (kind === 'not-found') return '找不到這則旅程通知。';
  if (kind === 'conflict') return '通知資料已更新，請重新載入後再試。';
  if (kind === 'ack-not-required') return '這則通知不需要確認收到。';
  if (kind === 'validation') return '通知請求不正確。';
  return cause?.raw?.message || cause?.message || fallback || '旅程通知無法載入。';
}

export function buildNoticeMutationBody(notice) {
  return {
    expectedNoticeRowVersion: notice.rowVersion,
    confirm: true,
  };
}

export function mergeNotice(list, updated) {
  return (Array.isArray(list) ? list : []).map((item) => (
    String(item.noticeId) === String(updated.noticeId) ? updated : item
  ));
}
